import { useTranslation } from "react-i18next";
import React, { useState } from 'react';
import { DateRangePicker } from './DateRangePicker';
import { GranularitySelector, Granularity } from './GranularitySelector';
import { DisputesFilters } from './DisputesFilters';
import { EmptyState } from './EmptyState';
import { ChevronDownIcon } from 'lucide-react';
interface DateRange {
  start: Date;
  end: Date;
}
interface DisputesTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function DisputesTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: DisputesTabProps) {
  const {
    t
  } = useTranslation("analytics");
  // New filter states
  const [disputeDateBasis, setDisputeDateBasis] = useState<'dispute-date' | 'payment-date'>('dispute-date');
  const [paymentCountBasis, setPaymentCountBasis] = useState<'payment-count' | 'payment-volume'>('payment-count');
  const [includeEFW, setIncludeEFW] = useState<'include' | 'exclude'>('include');
  // Existing filter states
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDisputeDateBasis('dispute-date');
    setPaymentCountBasis('payment-count');
    setIncludeEFW('include');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-1758" data-mp="yjt0aqlyin4y">
      {/* All Filters - Compact Layout */}
      <div className="space-y-3" data-id="element-1759" data-mp="12jl9n5j6cxbd">
        {/* First Row: Date Range and Granularity */}
        <div className="flex flex-wrap items-center gap-3" data-id="element-1760" data-mp="5v5d2phndnor">
          <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-1761" data-mp="atgjsrcnbi2r" />
          <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-1762" data-mp="atox2kcokpeh" />
          
        </div>

        {/* Disputes Filters (includes both rows) */}
        <DisputesFilters disputeDateBasis={disputeDateBasis} onDisputeDateBasisChange={setDisputeDateBasis} paymentCountBasis={paymentCountBasis} onPaymentCountBasisChange={setPaymentCountBasis} includeEFW={includeEFW} onIncludeEFWChange={setIncludeEFW} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-1763" data-mp="jmk51ltj8z7y" />
        
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1764" data-mp="31qi8x224fj5c">
        <div className="p-6 border-b border-gray-200" data-id="element-1765" data-mp="2wsgtcr3wm1ym">
          <div className="flex items-center justify-between" data-id="element-1766" data-mp="2wu1ks7ux94s9">
            <div className="flex items-center gap-4" data-id="element-1767" data-mp="2s1i8de7z0fp1">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-1768" data-mp="2rznmnxovkjjw">
                {t("components.DisputesTab.key_metrics_for_cards")}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1769" data-mp="2j23jgj7gcd6n">
                <span data-id="element-1770" data-mp="2e2ijc5gjpysc">{t("components.AuthenticationTab.compared_to")}</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1771" data-mp="2e3tq8e5afmno">
                  {t("components.AuthenticationTab.previous_period")}
                  <ChevronDownIcon size={16} data-id="element-1772" data-mp="hz4335k4nhe7" />
                </button>
                <span className="text-gray-500" data-id="element-1773" data-mp="39y8juw8ifcn">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-1774" data-mp="86oj62p9siqo">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1776" data-mp="3ufzmcb7l3n1z">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1775" data-mp="3qe4jh2a091m3" />
          
        </div>
      </div>

      {/* Card disputes breakdown */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1777" data-mp="1jo6f4g0m0e2n">
        <div className="p-6 border-b border-gray-200" data-id="element-1778" data-mp="1jnew04o788t9">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1779" data-mp="345pavl2nvcp5">
            {t("components.DisputesTab.card_disputes_breakdown")}
          </h2>
        </div>

        <div className="p-12" data-id="element-1780" data-mp="2z51f6m3hm7ol">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1782" data-mp="395lirvehbo9z">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1781" data-mp="3dwk110j12wfv" />
          
        </div>
      </div>

      {/* Monthly submission performance */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1783" data-mp="3rvtfnvrl5ihq">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-1784" data-mp="3rwl0cbjn9vt8">
          <div data-id="element-1785" data-mp="22hev7nnlohs1">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1786" data-mp="22izg0lk44mcp">
              {t("components.DisputesTab.monthly_submission_performance")}
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1787" data-mp="2n6zawmuefghw">
              {t("components.DisputesTab.a_detailed_view_of_submission_performance")}
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1788" data-mp="2w6vtc6q73j4i">
            {t("components.DisputesTab.all_reasons")}
            <ChevronDownIcon size={16} data-id="element-1789" data-mp="365lcs3xqpbmv" />
          </button>
        </div>

        <div className="p-12" data-id="element-1790" data-mp="3a6dl8uysfci3">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1792" data-mp="3ewloww1s88xh">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1791" data-mp="3a5lpnk56xert" />
          
        </div>
      </div>

      {/* Monthly dispute outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1793" data-mp="1i6rctl772ohz">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-1794" data-mp="1i4y3tz93zs6t">
          <div data-id="element-1795" data-mp="1d6mtlnewjfo2">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1796" data-mp="1daanwos6863u">
              {t("components.DisputesTab.monthly_dispute_outcomes")}
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1797" data-mp="13ati87cx5s7v">
              {t("components.DisputesTab.a_breakdown_of_how_card_disputes")}
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1798" data-mp="1vyfpbplcvezh">
            {t("components.DisputesTab.all_reasons")}
            <ChevronDownIcon size={16} data-id="element-1799" data-mp="1m95ui1z4ni7o" />
          </button>
        </div>

        <div className="p-12" data-id="element-1800" data-mp="2jo05y1u1e05l">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-1802" data-mp="2ewrxb80j6ith">
                
                  {t("components.AuthenticationTab.clear_filters")}
                </button>{' '}
                {t("components.AuthenticationTab.or_select_another_date_range_and")}
              </>} data-id="element-1801" data-mp="2jo9x3sqtidcv" />
          
        </div>
      </div>
    </div>;
}